import Styles from './QualityReport.module.scss';
import { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';

// Module imports.
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import ReactToPrint, { PrintContextConsumer } from 'react-to-print';

// Components import.
import { useAPI, Table, Card, Logo, AuthContext } from 'components/lib';
import { AivagamButton } from 'components/global/AivagamButton/AivagamButton';
import { LoadingBackdrop } from 'components/global/LoadingBackdrop/LoadingBackdrop';
import { BreadCrumbs } from 'components/global/BreadCrumbs/BreadCrumbs';
import { RadarChart } from './RadarChart/RadarChart';

export function QualityReport(props) {

    const { metricId } = useParams();
    const { data, loading } = useAPI(metricId ? `/api/user/metrics/${metricId}/report` : '/api/qq/report')
    const authContext = useContext(AuthContext); 

    const [feedbackTexts, setFeedbackTexts] = useState([])
    const [crumbData, setCrumbData] = useState({
        onClickPaths: [
            '/qq',
            ''
        ],
        crumbs: props.t('QualityQuotient.QualityReport.breadCrumbs', { returnObjects: true }),
    })

    useEffect(() => {
        if (data?.rangeBucketsText?.length) {
            setFeedbackTexts(data?.rangeBucketsText)
            setCrumbData({
                onClickPaths: [
                    '/user/metrics',
                    `/user/metrics/${metricId}/assessments`,
                    '',
                ],
                crumbs: [
                    'User Quizzes',
                    `${data?.name}: Assessments`,
                    `${data?.name} Report`,
                ]
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data])

    function getBucketText(score) {
        if (score >= 0 && score <= data?.range[0]) {
            return feedbackTexts[0];  // Low
        } else if (score > data?.range[0] && score <= data?.range[1]) {
            return feedbackTexts[1];  // Average
        } else if (score > data?.range[1] && score <= data?.range[2]) {
            return feedbackTexts[2];  // Good
        } else if (score > data?.range[2]) {
            return feedbackTexts[3];  // Exceptional
        }
    }

    const strongestText = (score) => {
        if (score > 90) {
            return props.t('QualityQuotient.QualityReport.strongestText.high');
        } else if (score >= 60) {
            return props.t('QualityQuotient.QualityReport.strongestText.medium');
        } else if (score < 60) {
            return props.t('QualityQuotient.QualityReport.strongestText.low');
        } else {
            return props.t('QualityQuotient.QualityReport.strongestText.default');
        }
    };

    const weakestText = (score) => {
        if (score >= 90) {
            return props.t('QualityQuotient.QualityReport.weakestText.high');
        } else if (score >= 60) {
            return props.t('QualityQuotient.QualityReport.weakestText.medium');
        } else if (score < 60) {
            return props.t('QualityQuotient.QualityReport.weakestText.low');
        } else {
            return props.t('QualityQuotient.QualityReport.weakestText.default');
        }
    };

    const getGrade = (score) => {

        if (score >= 90) {
            return "A+";
        } else if (score >= 80) {
            return "A  ";
        } else if (score >= 70) {
            return "B+";
        } else if (score >= 60) {
            return "B  ";
        } else if (score >= 50) {
            return "C+";
        } else if (score >= 40) {
            return "C  ";
        } else if (score >= 30) {
            return "D+";
        } else if (score >= 20) {
            return "D  ";
        } else {
            return "F  "; // Grade D for scores below 2 (or you can adjust this as needed)
        }
    };

    const [userScore, setUserScore] = useState(0);



    useEffect(() => {
        if (data) {
            const timer = setInterval(() => {
                setUserScore((prevProgress) => (
                    prevProgress >= data.overallScore ? data.overallScore : prevProgress + 1
                ));
            }, 25);
            return () => {
                clearInterval(timer);
            };
        }
    }, [data]);

    const [selectedChart, setSelectedChart] = useState('Bar');

    const handleChartChange = (event) => {
        setSelectedChart(event.target.value);
    };

    return (
        <>
            <LoadingBackdrop isOpen={loading} />
            <BreadCrumbs
                onClickPaths={crumbData.onClickPaths}
                crumbs={crumbData.crumbs}
            />
            <span id='qualityQuotientReport' className={Styles.qualityQuotientReport}>
                <style type="text/css" media="print">
                    {"\
   @page { size: landscape; }\
"}
                </style>
                <div id='ancestorContainer' className={Styles.ancestorContainer}>
                <div className={Styles.headerContent}>
                    <div className={Styles.userInfo}>
                            <p className={Styles.userName}>
                            Name: {authContext.user?.first_name} {authContext.user?.last_name}
                            </p>
                        </div>
                        </div>
                    <span className={Styles.headerStyles}>
                        <p className={Styles.lineWrap} style={{ width: "35ch", height: "2em" }}>
                            {
                                metricId ? data?.name
                                    : props.t('QualityQuotient.QualityReport.report.header')
                            }
                        </p>
                     
                        <span style={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center",  marginTop: "-5em"  }}>
                            <span>
                                <select className={Styles.chartDropdown} id="chartType" onChange={handleChartChange} value={selectedChart}>
                                    <option value="Radar">Radar</option>
                                    <option value="Bar">Bar</option>
                                    <option value="Line">Line</option>
                                    <option value="PolarArea">Polar Area</option>
                                </select>
                            </span>
                            <Logo
                                color="white"
                            />
                        </span>
                    </span>
                    {data &&
                        <div id='performanceContainer' className={Styles.performanceContainer}>
                            <div id='overviewContainer' className={Styles.overviewContainer}>
                                <div id='highlightsContainer' className={Styles.highlightsContainer}>
                                    <p className={Styles.lineWrap}>
                                        {props.t('QualityQuotient.QualityReport.report.strong')}
                                    </p>
                                    <p className={Styles.lineWrap}>
                                        {
                                            metricId ? getBucketText(data.weakestAverageScore)
                                                : strongestText(data.weakestAverageScore)
                                        }
                                    </p>
                                    <span>
                                        {data?.strongest?.map((area, index) => (
                                            <p key={`${index}-${area}`} className={Styles.pointsStyle}>
                                                <FeatherIcon
                                                    color='white'
                                                    icon='plus-circle'
                                                />
                                                {area}
                                            </p>
                                        ))}
                                    </span>
                                </div>
                                {data?.weakest?.length &&
                                    <div id='highlightsContainer' className={Styles.highlightsContainer}>
                                        <p className={Styles.lineWrap}
                                            style={{
                                                color: '#fec2aa'
                                            }}
                                        >
                                            {props.t('QualityQuotient.QualityReport.report.weak')}
                                        </p>
                                        <p className={Styles.lineWrap}>
                                            {metricId ? getBucketText(data.weakestAverageScore)
                                                : weakestText(data.weakestAverageScore)
                                            }
                                        </p>
                                        <span>
                                            {data.weakestAverageScore < 90 &&
                                                data?.weakest?.map((area, index) => (
                                                    <p key={`${index}-${area}`} className={Styles.pointsStyle}>
                                                        <FeatherIcon
                                                            color='white'
                                                            icon='minus-circle'
                                                        />
                                                        {area}
                                                    </p>
                                                ))
                                            }
                                        </span>
                                    </div>
                                }
                                <div id='scoreContainer' className={Styles.scoreContainer}>
                                    <div
                                        style={{ minWidth: `40%` }}
                                    >
                                        <p>
                                            {props.t('QualityQuotient.QualityReport.report.score')}
                                        </p>
                                        {userScore}
                                    </div>
                                    <div
                                        style={{ minWidth: `60%` }}
                                    >
                                        <p>
                                            {metricId ? props.t('QualityQuotient.QualityReport.report.metric')
                                                : props.t('QualityQuotient.QualityReport.report.qq')}
                                        </p>
                                        {getGrade(data?.overallScore)}
                                        <div
                                            style={{
                                                width: `${userScore}%`,
                                                backgroundColor: userScore < 60 ? "#ffb0cc" :
                                                    userScore < 90 ? "#eeffb0" :
                                                        "#b0ffb4"
                                            }}
                                            className={Styles.progressBarDiv}

                                        />
                                    </div>
                                </div>
                            </div>
                            <div id='spiderContainer' className={Styles.spiderContainer}>
                                <RadarChart
                                    data={data?.data}
                                    labels={data?.labels}
                                    selectedChart={selectedChart}
                                />
                            </div>
                        </div>
                    }
                </div>
                <div id='scoresListContainer' className={Styles.scoresListContainer}>
                    <Logo />
                    <Card
                        // title={"Score Card"}
                        loading={loading}
                        center
                        shadow
                    // restrictWidth
                    >
                        <Table
                            data={data?.userData}
                            show={['name', 'percentage']}
                            translation='QualityQuotient.QualityReport.table'
                        />
                    </Card>
                </div>
            </span>
            <div id='buttonContainer' className={Styles.buttonContainer}>
                <div id='scoreRead' className={Styles.scoreRead}>
                    <strong>
                        {props.t('QualityQuotient.QualityReport.info.title')} </strong>
                    {metricId ? (
                        <>
                            {(data?.rangeBuckets?.length !== 0 && data?.range?.length !== 0) &&
                                <>
                                    <p>
                                        <strong>{`0 to ${data?.range[0]}: `}</strong>
                                        {data?.rangeBuckets[0]}
                                    </p>
                                    <p>
                                        <strong>{`${data?.range[0]} to ${data?.range[1]}: `}</strong>
                                        {data?.rangeBuckets[1]}
                                    </p>
                                    <p>
                                        <strong>{`${data?.range[1]} to ${data?.range[2]}: `}</strong>
                                        {data?.rangeBuckets[2]}
                                    </p>
                                    <p>
                                        <strong>{`Above ${data?.range[2]}: `}</strong>
                                        {data?.rangeBuckets[3]}
                                    </p>
                                </>
                            }
                        </>
                    ) : (
                        <>
                            <p>
                                <strong>{props.t('QualityQuotient.QualityReport.info.beginner.title')}</strong>
                                {props.t('QualityQuotient.QualityReport.info.beginner.descr')}
                            </p>
                            <p>
                                <strong>{props.t('QualityQuotient.QualityReport.info.intermediate.title')}</strong>
                                {props.t('QualityQuotient.QualityReport.info.intermediate.descr')}
                            </p>
                            <p>
                                <strong>{props.t('QualityQuotient.QualityReport.info.master.title')}</strong>
                                {props.t('QualityQuotient.QualityReport.info.master.descr')}
                            </p>
                        </>
                    )}
                </div>
                <ReactToPrint
                    copyStyles={true}
                    content={() => document.getElementById('qualityQuotientReport')}
                    // onAfterPrint={() => { setDownloadOpen(!downloadOpen) }}
                    documentTitle='quality_quotient_report'
                >
                    <PrintContextConsumer>
                        {({ handlePrint }) => (
                            <AivagamButton
                                icon
                                buttonText={
                                    <>
                                        <FeatherIcon
                                            color='white'
                                            icon='download'
                                        />
                                        {props.t('QualityQuotient.QualityReport.button')} </>
                                }
                                onClick={handlePrint}
                                type={"confirm"}
                            />
                        )}
                    </PrintContextConsumer>
                </ReactToPrint>
            </div>
        </>
    );
}